import cn from 'classnames';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../edit.module.scss';

interface MedicationSummaryProps {
    data?: any;
    heading?: ReactNode;
    className?: string;
}

const normalizeValue = (value: unknown, fallback = '—'): ReactNode => {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }

    if (Array.isArray(value)) {
        return value.length ? value.join(', ') : fallback;
    }

    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    return value as ReactNode;
};

export const MedicationSummary: FC<MedicationSummaryProps> = ({
    data,
    heading,
    className,
}) => {
    const { t } = useTranslation();

    if (!data) {
        return null;
    }

    const items: Array<{ key: string; label: ReactNode; value: ReactNode }> = [
        {
            key: 'name',
            label: t('applications.fields.name_of_medicine'),
            value: normalizeValue(data.medicationName),
        },
        {
            key: 'trade',
            label: t('applications.fields.trade_name'),
            value: normalizeValue(data.tradeName ?? data.medication_name),
        },
        {
            key: 'pharmaceutical_form',
            label: t('applications.fields.pharmaceutical_form'),
            value: normalizeValue(data.pharmaceuticalForm ?? data.pharmaceuticalFormName),
        },
        {
            key: 'release_form',
            label: t('applications.fields.release_form'),
            value: normalizeValue(data.releaseForm),
        },
        {
            key: 'manufacturers',
            label: t('applications.fields.manufacturers'),
            value: normalizeValue(data.manufacturers),
        },
        {
            key: 'inn',
            label: t('applications.fields.inn'),
            value: normalizeValue(data.internationalNameIds ?? data.inn),
        },
        {
            key: 'atx',
            label: t('applications.fields.atx'),
            value: normalizeValue(data.atxCode ?? data.atx),
        },
        {
            key: 'usage',
            label: t('applications.fields.usage'),
            value: normalizeValue(data.usage),
        },
        {
            key: 'storage_period',
            label: t('applications.fields.storage_period'),
            value: normalizeValue(data.storagePeriod),
        },
    ];

    const card = (
        <section
            className={cn(styles.sectionCard, styles.medicineSummary, className)}
        >
            <div className={styles.medicineInfoGrid}>
                {items.map(item => (
                    <div
                        key={item.key}
                        className={cn(styles.infoItem, styles.medicineInfoItem)}
                    >
                        <span className={styles.infoLabel}>{item.label}</span>
                        <span className={styles.infoValue}>{item.value}</span>
                    </div>
                ))}
            </div>
        </section>
    );

    if (!heading) {
        return card;
    }

    return (
        <div className={styles.sectionBlock}>
            <div className={styles.sectionHeadingRow}>
                <span className={styles.sectionNumberFilled}>1</span>
                <div className={styles.sectionHeadingGroup}>
                    <h2 className={styles.sectionHeading}>{heading}</h2>
                </div>
            </div>
            {card}
        </div>
    );
};

export default MedicationSummary;
